import {Check, Rocket, TrendingUp, X, Zap} from 'lucide-react'
import { Link } from 'react-router-dom'
import Layout from '@/components/Layout'
import SeoHead from '@/components/SeoHead'
import {
  CREATOR_SHARE_BPS,
  GRADUATION_QUOTE_ETH,
  LAUNCH_FEE_ETH,
  PLATFORM_SHARE_BPS,
  TRADE_FEE_BPS,
} from '@/lib/launchpad/constants'
import {
  DEFAULT_MAX_CONTRIBUTION_ETH,
  DEFAULT_MIN_CONTRIBUTION_ETH,
  PRESALE_CREATOR_MAX_PCT,
  PRESALE_DEFAULT_HARD_CAP_ETH,
  PRESALE_DEFAULT_SOFT_CAP_ETH,
  PRESALE_FEE_BPS,
  PRESALE_LAUNCH_FEE_ETH,
  PRESALE_LP_MIN_PCT,
  formatBps,
} from '@/lib/protocol'

const quickLaunchPoints = [
  { label: 'Live now', body: `Trading opens the moment you launch. No start date, no waiting.` },
  { label: `${LAUNCH_FEE_ETH} ETH to launch`, body: 'The cheapest way to get a token trading on Penguin Pad.' },
  { label: 'Bonding curve pricing', body: `Price rises automatically as buyers come in. Graduates into LP at ${GRADUATION_QUOTE_ETH} ETH net raised.` },
  { label: 'Anyone can buy instantly', body: 'No caps, no allowlist, no minimum or maximum raise — the curve sets the price.' },
  { label: `${formatBps(TRADE_FEE_BPS)} trade fee`, body: `Split ${formatBps(CREATOR_SHARE_BPS)} creator / ${formatBps(PLATFORM_SHARE_BPS)} platform. Curve fees are disbursed at bonding; after listing, the same split is paid to your wallet on every Uniswap swap.` },
  { label: 'No fixed target', body: 'There is no soft cap to hit and no risk of a failed raise — trading either happens or it does not.' },
]

const presalePoints = [
  { label: 'Scheduled raise', body: 'Pick a start (at least 20 minutes out) and end date. Contributions only count inside that window.' },
  { label: `${PRESALE_LAUNCH_FEE_ETH} ETH to open`, body: 'Higher upfront cost, but you control the raise structure end to end.' },
  { label: 'Soft cap / hard cap', body: `You set the targets (defaults: ${PRESALE_DEFAULT_SOFT_CAP_ETH} ETH soft cap / ${PRESALE_DEFAULT_HARD_CAP_ETH} ETH hard cap). Hitting soft cap means the raise can succeed.` },
  { label: 'Per-wallet limits', body: `You set min/max contribution per wallet (defaults: ${DEFAULT_MIN_CONTRIBUTION_ETH}\u2013${DEFAULT_MAX_CONTRIBUTION_ETH} ETH), so no single wallet can dominate the raise.` },
  { label: `${formatBps(PRESALE_FEE_BPS)} platform fee on success`, body: `Only charged if the raise succeeds. Remaining ETH splits ${PRESALE_LP_MIN_PCT}\u2013100% to LP and up to ${PRESALE_CREATOR_MAX_PCT}% to you at listing.` },
  { label: '100% refundable if it fails', body: 'If raised stays below the soft cap, every contributor can refund in full. No tokens are issued.' },
  { label: 'Post-raise trading fee', body: `Once your token lists on Uniswap, every swap pays a ${formatBps(TRADE_FEE_BPS)} fee split ${formatBps(CREATOR_SHARE_BPS)} to you / ${formatBps(PLATFORM_SHARE_BPS)} platform, disbursed in that same transaction.` },
]

interface ComparisonRow {
  feature: string
  quickLaunch: string
  presale: string
}

const comparisonRows: ComparisonRow[] = [
  { feature: 'Best for', quickLaunch: 'Fast, fair-launch memes and community tokens', presale: 'Serious projects that need committed capital before launch' },
  { feature: 'Cost to create', quickLaunch: `${LAUNCH_FEE_ETH} ETH`, presale: `${PRESALE_LAUNCH_FEE_ETH} ETH` },
  { feature: 'Starts trading', quickLaunch: 'Instantly', presale: `After a 20+ min buffer, at your scheduled start` },
  { feature: 'Price discovery', quickLaunch: 'Bonding curve (automatic)', presale: 'Fixed rate (auto-calculated)' },
  { feature: 'Funding target', quickLaunch: 'None — curve trades either way', presale: `Soft cap ${PRESALE_DEFAULT_SOFT_CAP_ETH} ETH / hard cap ${PRESALE_DEFAULT_HARD_CAP_ETH} ETH by default` },
  { feature: 'Risk if it flops', quickLaunch: 'Token just trades thin, no refunds needed', presale: 'Raise fails below soft cap, 100% refundable' },
  { feature: 'Per-wallet caps', quickLaunch: 'None', presale: 'Creator-set min/max per wallet' },
  { feature: 'Ongoing trade fee', quickLaunch: `${formatBps(TRADE_FEE_BPS)}, ${formatBps(CREATOR_SHARE_BPS)}/${formatBps(PLATFORM_SHARE_BPS)} split`, presale: `${formatBps(TRADE_FEE_BPS)} after listing, same split` },
  { feature: 'Creator payout', quickLaunch: 'Disbursed at bonding, then on every Uniswap swap', presale: 'Raise share at finalize; Uniswap fees on every swap' },
  { feature: 'Cancelable', quickLaunch: 'No — once live, it trades', presale: 'Yes, before start or with 24h+ left' },
]

function PresaleVsQuickLaunch() {
  return (
    <Layout>
      <SeoHead
        title="Presale vs. Quick Launch"
        description="Compare Penguin Pad's Quick Launch bonding curves and Presale raises side by side — fees, caps, timing, and payouts — to pick the right launch path."
      />

      <section>
        <div className="mx-auto max-w-5xl px-4 pb-2 pt-12 sm:px-6 lg:px-8">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-lime-400">Playbook</p>
          <h1 className="mt-1 text-3xl font-bold text-white sm:text-4xl">Presale vs. Quick Launch</h1>
          <p className="mt-2 max-w-2xl text-sm text-zinc-400">
            Two permissionless paths to launch on Penguin Pad. Quick Launch gets a bonding-curve token trading
            instantly with no cap. Presale lets you run a scheduled raise with a soft/hard cap before your token
            ever lists. Compare the two below to pick the right route.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        {/* Side-by-side feature cards */}
        <div className="grid gap-5 lg:grid-cols-2">
          <div className="flex flex-col rounded-3xl border border-lime-400/20 bg-lime-400/5 p-6 backdrop-blur-md">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-lime-400/20 bg-lime-400/10 text-lime-400">
                <Rocket className="h-5 w-5" aria-hidden="true" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Quick Launch</h2>
                <p className="text-xs text-zinc-500">Bonding curve, live instantly</p>
              </div>
            </div>
            <ul className="mt-5 space-y-3.5">
              {quickLaunchPoints.map(point => (
                <li key={point.label} className="flex gap-2.5">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-lime-400" aria-hidden="true" />
                  <div>
                    <p className="text-sm font-semibold text-white">{point.label}</p>
                    <p className="mt-0.5 text-sm leading-relaxed text-zinc-400">{point.body}</p>
                  </div>
                </li>
              ))}
            </ul>
            <Link
              to="/submit"
              className="mt-6 inline-flex items-center justify-center rounded-full bg-lime-400 px-5 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-lime-300"
            >
              Launch a token now
            </Link>
          </div>

          <div className="flex flex-col rounded-3xl border border-orange-400/20 bg-orange-400/5 p-6 backdrop-blur-md">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-orange-400/20 bg-orange-400/10 text-orange-400">
                <TrendingUp className="h-5 w-5" aria-hidden="true" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Presale</h2>
                <p className="text-xs text-zinc-500">Scheduled raise, soft/hard cap</p>
              </div>
            </div>
            <ul className="mt-5 space-y-3.5">
              {presalePoints.map(point => (
                <li key={point.label} className="flex gap-2.5">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-orange-400" aria-hidden="true" />
                  <div>
                    <p className="text-sm font-semibold text-white">{point.label}</p>
                    <p className="mt-0.5 text-sm leading-relaxed text-zinc-400">{point.body}</p>
                  </div>
                </li>
              ))}
            </ul>
            <Link
              to="/submit"
              className="mt-6 inline-flex items-center justify-center rounded-full border border-orange-400/40 px-5 py-2.5 text-sm font-semibold text-orange-400 transition-colors hover:bg-orange-400/10"
            >
              Open a presale
            </Link>
          </div>
        </div>

        {/* Comparison table - desktop */}
        <div className="mt-12 hidden overflow-hidden rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-md md:block">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-white/[0.03]">
                <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500">Feature</th>
                <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.1em] text-lime-400">Quick Launch</th>
                <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.1em] text-orange-400">Presale</th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row, i) => (
                <tr key={row.feature} className={i % 2 === 1 ? 'bg-white/[0.015]' : ''}>
                  <td className="border-t border-white/5 px-5 py-3.5 font-semibold text-white">{row.feature}</td>
                  <td className="border-t border-white/5 px-5 py-3.5 text-zinc-400">{row.quickLaunch}</td>
                  <td className="border-t border-white/5 px-5 py-3.5 text-zinc-400">{row.presale}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Comparison list - mobile */}
        <div className="mt-12 space-y-3 md:hidden">
          <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500">Side-by-side</h3>
          {comparisonRows.map(row => (
            <div key={row.feature} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <p className="text-sm font-semibold text-white">{row.feature}</p>
              <div className="mt-3 grid grid-cols-1 gap-3">
                <div className="rounded-xl border border-lime-400/15 bg-lime-400/5 p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-lime-400">Quick Launch</p>
                  <p className="mt-1 text-sm text-zinc-300">{row.quickLaunch}</p>
                </div>
                <div className="rounded-xl border border-orange-400/15 bg-orange-400/5 p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-orange-400">Presale</p>
                  <p className="mt-1 text-sm text-zinc-300">{row.presale}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Decision helper */}
        <div className="mt-12 grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-lime-400" aria-hidden="true" />
              <p className="text-sm font-semibold text-white">Pick Quick Launch if...</p>
            </div>
            <ul className="mt-3 space-y-2 text-sm text-zinc-400">
              <li className="flex gap-2"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-lime-400" aria-hidden="true" />You want to be live and tradable in minutes.</li>
              <li className="flex gap-2"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-lime-400" aria-hidden="true" />You want the market, not a fixed target, to set the price.</li>
              <li className="flex gap-2"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-lime-400" aria-hidden="true" />You are launching a fair-launch or meme-style token.</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <div className="flex items-center gap-2">
              <X className="h-4 w-4 text-orange-400" aria-hidden="true" />
              <p className="text-sm font-semibold text-white">Pick Presale if...</p>
            </div>
            <ul className="mt-3 space-y-2 text-sm text-zinc-400">
              <li className="flex gap-2"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-orange-400" aria-hidden="true" />You need a set amount of ETH raised before you go live.</li>
              <li className="flex gap-2"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-orange-400" aria-hidden="true" />You want per-wallet caps so no single buyer dominates.</li>
              <li className="flex gap-2"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-orange-400" aria-hidden="true" />You want full refunds to contributors if the raise falls short.</li>
            </ul>
          </div>
        </div>

        <div className="mt-10 flex flex-col items-start gap-3 rounded-3xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-md sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-zinc-300">Still deciding? Read the full playbook or jump straight into a launch.</p>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/how-it-works"
              className="rounded-full border border-white/10 px-4 py-2 text-sm font-medium text-white hover:border-white/20"
            >
              How it works
            </Link>
            <Link
              to="/submit?type=presale"
              className="rounded-full bg-lime-400 px-4 py-2 text-sm font-semibold text-black hover:bg-lime-300"
            >
              Start a launch
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  )
}

export default PresaleVsQuickLaunch
